import ExpandMoreIcon from '@material-design-icons/svg/filled/expand_more.svg?react';
import { useMemo } from 'react';
import type { UseRejectionReasonsResult } from '../../../hooks/useRejectionReasons';
import { SearchableMultiSelect } from '../../SearchableMultiSelect';
import {
  fieldLabelClassName,
  panelInputClassName,
  panelSelectClassName,
  primaryActionClassName,
  secondaryActionClassName,
} from '../controlStyles';
import type { HubspotTicketStageOptionsResponse } from '../types';
import { ReviewDialogShell } from './ReviewDialogShell';

interface HubspotTicketStatusEditorProps {
  hsPipelineStage: string | null;
  statusPipelineLabel: string | null;
  statusOptions: HubspotTicketStageOptionsResponse['options'];
  selectedStageId: string;
  currentStageId: string | null;
  statusError: string | null;
  isStatusLoading: boolean;
  isStatusSaving: boolean;
  isClosingStage: boolean;
  isRejectionClosure: boolean;
  closureCategory: string;
  closureLabel: string;
  closureRejectionReasons: string[];
  rejectionReasonState: UseRejectionReasonsResult;
  reviewerNote: string;
  isChecklistBlocking: boolean;
  checklistHelperText: string | null;
  onSelectedStageChange: (stageId: string) => void;
  onClosureCategoryChange: (value: string) => void;
  onClosureLabelChange: (value: string) => void;
  onToggleClosureRejectionReason: (code: string) => void;
  onReviewerNoteChange: (value: string) => void;
  onSaveStatus: () => void;
}

/**
 * Stage picker for admins. Non-closing changes save inline; picking a closing
 * stage opens a dialog for the closure record (reasons or category/label plus
 * the reviewer note) so the form never fights the panel for space.
 */
export function HubspotTicketStatusEditor({
  statusPipelineLabel,
  statusOptions,
  selectedStageId,
  currentStageId,
  statusError,
  isStatusLoading,
  isStatusSaving,
  isClosingStage,
  isRejectionClosure,
  closureCategory,
  closureLabel,
  closureRejectionReasons,
  rejectionReasonState,
  reviewerNote,
  isChecklistBlocking,
  checklistHelperText,
  onSelectedStageChange,
  onClosureCategoryChange,
  onClosureLabelChange,
  onToggleClosureRejectionReason,
  onReviewerNoteChange,
  onSaveStatus,
}: HubspotTicketStatusEditorProps) {
  const isDirty =
    Boolean(selectedStageId) && selectedStageId !== currentStageId;
  const isSaveDisabled =
    isStatusLoading || isStatusSaving || !isDirty || isChecklistBlocking;
  const isClosureDialogOpen = isClosingStage && isDirty;
  const selectedStage = statusOptions.find(
    (option) => option.value === selectedStageId,
  );
  const saveAriaLabel = isStatusSaving ? 'Saving Status' : 'Save Status';

  // Show active reasons, plus any already-selected reason even if it has since
  // been retired, so historical closure selections persist.
  const reasonChoices = useMemo(
    () =>
      rejectionReasonState.rejectionReasonOptions
        .filter(
          (reason) =>
            reason.isActive || closureRejectionReasons.includes(reason.code),
        )
        .map((reason) => ({
          key: reason.code,
          label: reason.label,
          category: reason.category,
        })),
    [rejectionReasonState.rejectionReasonOptions, closureRejectionReasons],
  );

  // The multi-select emits the full next selection; bridge it to the parent's
  // single-code toggle without changing the closure state's API.
  const handleClosureReasonsChange = (next: string[]) => {
    const current = new Set(closureRejectionReasons);
    const nextSet = new Set(next);
    closureRejectionReasons.forEach((code) => {
      if (!nextSet.has(code)) {
        onToggleClosureRejectionReason(code);
      }
    });
    next.forEach((code) => {
      if (!current.has(code)) {
        onToggleClosureRejectionReason(code);
      }
    });
  };

  const cancelClosure = () => onSelectedStageChange(currentStageId ?? '');

  const helper = statusError ? (
    <div className="text-xs text-red-400">{statusError}</div>
  ) : checklistHelperText ? (
    <div className="text-xs text-neutral-500">{checklistHelperText}</div>
  ) : null;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:w-52 sm:flex-none">
          <select
            aria-label="Stage"
            title={statusPipelineLabel ?? undefined}
            value={selectedStageId}
            onChange={(event) => onSelectedStageChange(event.target.value)}
            disabled={
              isStatusLoading || isStatusSaving || statusOptions.length === 0
            }
            className={panelSelectClassName}
            style={{ colorScheme: 'dark' }}
          >
            {statusOptions.length === 0 ? (
              <option value="">
                {isStatusLoading ? 'Loading…' : 'No statuses'}
              </option>
            ) : (
              statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                  {option.isClosed ? ' (Closed)' : ''}
                </option>
              ))
            )}
          </select>
          <ExpandMoreIcon className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 fill-current text-neutral-500" />
        </div>
        {isDirty && !isClosingStage && (
          <button
            type="button"
            onClick={onSaveStatus}
            disabled={isSaveDisabled}
            aria-label={saveAriaLabel}
            className={primaryActionClassName}
          >
            {isStatusSaving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>

      {!isClosureDialogOpen && helper}

      {isClosureDialogOpen && (
        <ReviewDialogShell
          title={`Close as ${selectedStage?.label ?? 'closed'}`}
          onClose={cancelClosure}
          closeDisabled={isStatusSaving}
          footer={
            <>
              <button
                type="button"
                onClick={cancelClosure}
                disabled={isStatusSaving}
                className={`${secondaryActionClassName} flex-1 sm:flex-none`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onSaveStatus}
                disabled={isSaveDisabled}
                aria-label={saveAriaLabel}
                className={`${primaryActionClassName} flex-1 sm:flex-none`}
              >
                {isStatusSaving ? 'Saving…' : 'Close ticket'}
              </button>
            </>
          }
        >
          <p className="text-sm text-neutral-400">
            {isRejectionClosure
              ? 'Choose the rejection reason(s) and write a reviewer note. The developer is notified by email.'
              : 'Record a category, label, and reviewer note for this closure.'}
          </p>

          {isRejectionClosure ? (
            <div className="space-y-1.5">
              <span className={fieldLabelClassName}>Rejection Reasons</span>
              {rejectionReasonState.isRejectionReasonsLoading ? (
                <div className="text-sm text-neutral-500">Loading reasons…</div>
              ) : rejectionReasonState.rejectionReasonsError ? (
                <div className="text-sm text-red-400">
                  {rejectionReasonState.rejectionReasonsError}
                </div>
              ) : (
                <SearchableMultiSelect
                  choices={reasonChoices}
                  value={closureRejectionReasons}
                  onChange={handleClosureReasonsChange}
                  disabled={isStatusSaving}
                  groupBy={(choice) => choice.category}
                  maxVisibleSelections={6}
                  placeholder="Select rejection reason(s)"
                  searchPlaceholder="Search reasons…"
                />
              )}
            </div>
          ) : (
            <div className="grid gap-3 text-sm sm:grid-cols-2">
              <label className="space-y-1.5">
                <span className={fieldLabelClassName}>Category</span>
                <input
                  aria-label="Category"
                  value={closureCategory}
                  onChange={(event) =>
                    onClosureCategoryChange(event.target.value)
                  }
                  maxLength={120}
                  className={panelInputClassName}
                  placeholder="Security, metadata, policy…"
                />
              </label>
              <label className="space-y-1.5">
                <span className={fieldLabelClassName}>Label</span>
                <input
                  aria-label="Label"
                  value={closureLabel}
                  onChange={(event) => onClosureLabelChange(event.target.value)}
                  maxLength={120}
                  className={panelInputClassName}
                  placeholder="Approved, duplicate, needs rework…"
                />
              </label>
            </div>
          )}

          <label className="block space-y-1.5 text-sm">
            <span className={fieldLabelClassName}>Reviewer Note</span>
            <textarea
              aria-label="Reviewer Note"
              value={reviewerNote}
              onChange={(event) => onReviewerNoteChange(event.target.value)}
              rows={4}
              maxLength={5000}
              className={`${panelInputClassName} resize-y`}
              placeholder="Summarize why this ticket is being closed."
            />
          </label>

          {helper}
        </ReviewDialogShell>
      )}
    </div>
  );
}
